'use client'

import { RequiredFormStar } from '@/svg'
import { ErrorMessage } from '@hookform/error-message'
import { Avatar } from 'components/atoms'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { DynamicControl } from './DynamicControl'
import { FIELDNAMES } from './utils/constants'
import {
  isFieldRequired as checkIsFieldRequired,
  evaluateCondition,
} from './utils/functions'

const {
  DONATION_LETTER_RECIPIENT_COURTESY_TITLE,
  DONATION_LETTER_RECIPIENT_NAME,
} = FIELDNAMES

function getDynamicFieldProps(fieldProps, formValues) {
  if (
    fieldProps.fieldName === DONATION_LETTER_RECIPIENT_NAME &&
    formValues[DONATION_LETTER_RECIPIENT_COURTESY_TITLE] === 'Famiglia'
  ) {
    return {
      ...fieldProps,
      label: 'Cognome destinatario',
      placeholder: 'Inserisci cognome destinatario',
    }
  }

  return fieldProps
}

const Fieldset = ({
  className,
  fieldProps: _fieldProps,
  errors,
  currentStep,
  tabIndex,
  disableQueryState = false,
  useFormContextIfAvailable = true,
  isWatched,
  isFieldInView,
}) => {
  const {
    watch,
    unregister,
    getValues,
    formState: { defaultValues },
    ...rest
  } = useFormContext()

  // Track previous visibility state to detect transitions
  const prevVisibilityRef = useRef(null)
  const {
    uiOptions = {},
    subLabel,
    label,
    fieldName,
    defaultValue,
    config,
  } = _fieldProps

  const fieldProps = {
    ..._fieldProps,
    defaultValue: defaultValues[fieldName] || defaultValue,
  }

  const isFieldWatching = uiOptions.watchFields?.length > 0
  const showAvatars = uiOptions.showAvatars === true
  const showLabel = uiOptions.showLabel !== false
  const showSubLabel = uiOptions.showSubLabel !== false && subLabel
  const isHidden = uiOptions.hidden || false

  const currentFormValues = watch()
  const isFieldRequiredNow = checkIsFieldRequired(config, currentFormValues)

  const generateWatchList = () => {
    const watchList =
      uiOptions?.watchFields?.map((watchField) => {
        return evaluateCondition(watchField, currentFormValues)
      }) || []
    return watchList
  }

  const fieldWatchList = isFieldWatching || isWatched ? generateWatchList() : []

  const shouldShowFieldNow = fieldWatchList.every(Boolean) && !isHidden

  useEffect(() => {
    const wasVisible = prevVisibilityRef.current

    if (wasVisible === shouldShowFieldNow) return

    if (!shouldShowFieldNow) {
      unregister(fieldName, { keepValue: true, keepError: false })
    }
    prevVisibilityRef.current = shouldShowFieldNow
  }, [fieldName, shouldShowFieldNow, unregister])

  if (!shouldShowFieldNow) return null

  const resolvedFieldProps = getDynamicFieldProps(fieldProps, currentFormValues)

  return (
    <div className={`${className} show ${errors[fieldName] ? 'error' : ''}`}>
      {showAvatars && <Avatar numberOfAvatar={3} />}
      {showLabel && (
        <label
          id={`${fieldName}-label`}
          className={`${className}_label`}
          htmlFor={fieldName}
        >
          {resolvedFieldProps.label}
          {isFieldRequiredNow ? <RequiredFormStar /> : null}
        </label>
      )}
      {showSubLabel && (
        <span className={`${className}_sublabel`}>{subLabel}</span>
      )}
      <DynamicControl
        {...resolvedFieldProps}
        disableQueryState={disableQueryState}
        isHidden={false}
        useFormContextIfAvailable={useFormContextIfAvailable}
        triggerError={errors[fieldName]}
      />
      {errors[fieldName] && (
        <span className={`${className}_error`}>
          <ErrorMessage
            errors={errors}
            name={fieldName}
          />
        </span>
      )}
    </div>
  )
}

export default Fieldset
